// HqConfirmDialog — the branded replacement for native window.confirm().
//
// Two exports:
//   · <HqConfirmMount/> — a single portal-mount that must appear ONCE
//     at the app root. Consumes queued confirm requests and renders
//     the modal.
//   · hqConfirm(opts) — a module-level async function any code can
//     call. Returns Promise<boolean>. Resolves true on confirm,
//     false on cancel / Escape / backdrop click.
//
// Usage:
//   if (!(await hqConfirm({
//     title: 'Cancel subscription?',
//     body: 'You keep access until the end of the current period.',
//     confirmLabel: 'Cancel subscription',
//     danger: true,
//   }))) return;
//
// The calling function MUST be async. Sync fallback: if HqConfirmMount
// isn't mounted (rare — misconfigured app root), we silently fall back
// to window.confirm() so no destructive action ever fires without
// operator consent.

import React, { useEffect, useRef, useState } from 'react';
import hq from './theme';

// ─── Module-level queue + subscriber ────────────────────────────────
let listener = null;
const pending = [];

function emit(req) {
  if (listener) {
    listener(req);
  } else {
    pending.push(req);
  }
}

export function hqConfirm(opts = {}) {
  return new Promise((resolve) => {
    // Fallback: no mount yet — degrade to browser confirm so we never
    // silently discard a destructive action.
    if (!listener && pending.length > 20) {
      resolve(window.confirm(opts.body || opts.title || 'Are you sure?'));
      return;
    }
    emit({
      id: `${Date.now()}-${Math.random()}`,
      title:        opts.title || 'Confirm',
      body:         opts.body || '',
      confirmLabel: opts.confirmLabel || (opts.danger ? 'Confirm' : 'Continue'),
      cancelLabel:  opts.cancelLabel  || 'Cancel',
      danger:       Boolean(opts.danger),
      resolve,
    });
  });
}

// ─── The mount that consumes the queue and shows the modal ─────────
const CSS = `
@keyframes hqcd-in    { from { opacity: 0; } to { opacity: 1; } }
@keyframes hqcd-panel { from { opacity: 0; transform: translateY(8px) scale(.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
.hqcd-back  { animation: hqcd-in 180ms ease forwards; }
.hqcd-panel { animation: hqcd-panel 240ms cubic-bezier(.2,.7,.2,1) forwards; }
@media (prefers-reduced-motion: reduce) { .hqcd-back, .hqcd-panel { animation: none; opacity: 1; transform: none; } }
`;

export function HqConfirmMount() {
  const [req, setReq] = useState(null);
  const cancelBtnRef = useRef(null);
  const confirmBtnRef = useRef(null);

  useEffect(() => {
    listener = (r) => {
      if (req) {
        // Serial: queue by re-emitting until the current one resolves.
        // For our scale (destructive confirms fire one at a time) this
        // never actually happens.
        pending.push(r);
      } else {
        setReq(r);
      }
    };
    // Drain anything queued before the mount landed
    while (pending.length && !req) {
      const r = pending.shift();
      setReq(r);
      break;
    }
    return () => { listener = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!req) return;
    // Auto-focus the SAFE choice (Cancel) so a stray Enter doesn't
    // fire the destructive path.
    const t = setTimeout(() => cancelBtnRef.current?.focus(), 60);

    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); resolve(false); }
      else if (e.key === 'Enter' && document.activeElement === confirmBtnRef.current) {
        // Only Enter on the confirm button confirms — Enter elsewhere
        // does nothing so accidental typing can't trigger it.
        e.preventDefault(); resolve(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => { clearTimeout(t); window.removeEventListener('keydown', onKey); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [req?.id]);

  const resolve = (ok) => {
    if (!req) return;
    const done = req.resolve;
    setReq(pending.shift() || null);
    done(ok);
  };

  if (!req) return null;

  const accent = req.danger ? '#C4584A' : hq.color.amber;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="hqcd-title"
      className="hqcd-back"
      onClick={(e) => { if (e.target === e.currentTarget) resolve(false); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 20001,
        background: 'rgba(5,7,9,0.7)',
        backdropFilter: 'blur(10px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      <style>{CSS}</style>
      <div
        className="hqcd-panel"
        style={{
          width: 'min(460px, 100%)',
          background: hq.color.panel,
          border: `1px solid ${hq.color.line}`,
          borderTop: `2px solid ${accent}`,
          borderRadius: 14,
          padding: 22,
          boxShadow: '0 30px 80px -20px rgba(0,0,0,0.7)',
          fontFamily: hq.type.body, color: hq.color.text,
        }}
      >
        <div id="hqcd-title" style={{
          fontFamily: hq.type.display, fontSize: 20, fontWeight: 700,
          color: hq.color.text, letterSpacing: '-0.015em',
        }}>{req.title}</div>

        {req.body && (
          <div style={{ marginTop: 10, fontSize: 14, lineHeight: 1.55, color: hq.color.textSec }}>
            {req.body}
          </div>
        )}

        <div style={{ marginTop: 22, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            ref={cancelBtnRef}
            onClick={() => resolve(false)}
            style={{
              padding: '10px 16px', borderRadius: 8,
              background: 'transparent', border: `1px solid ${hq.color.line}`,
              color: hq.color.text, fontWeight: 500, fontSize: 13,
              cursor: 'pointer', fontFamily: hq.type.body,
            }}
          >{req.cancelLabel}</button>
          <button
            ref={confirmBtnRef}
            onClick={() => resolve(true)}
            style={{
              padding: '10px 16px', borderRadius: 8,
              background: req.danger
                ? `linear-gradient(180deg, #DA6B5C, ${accent})`
                : `linear-gradient(180deg, ${hq.color.bright}, ${hq.color.amber})`,
              border: 'none',
              color: '#0a0a0a', fontWeight: 700, fontSize: 13,
              cursor: 'pointer', fontFamily: hq.type.body,
              boxShadow: `0 6px 20px -8px ${accent}`,
            }}
          >{req.confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

export default HqConfirmMount;
